import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

// Styles
import './styles/modal.css';
import './styles/header.css';
import './styles/storage.css';
import './styles/dropzone.css';
import './styles/controls.css';
import './styles/file-list.css';
import './components/PreviewPanel/styles/index.css';
import './styles/note-input.css';
import './styles/delete-confirm.css';

// Components
import DropZone from './components/DropZone';
import StorageIndicator from './components/StorageIndicator';
import FilterControls from './components/FilterControls';
import FileList from './components/FileList';
import PreviewPanel from './components/PreviewPanel';
import NoteInput from './components/NoteInput';
import DeleteConfirmModal from './components/DeleteConfirmModal';

// Hooks
import useFileUpload from './hooks/useFileUpload';
import useAttachmentsData from './hooks/useAttachmentsData';
import useAttachmentsActions from './hooks/useAttachmentsActions';

/**
 * AttachmentsModal Component
 * 
 * Full-featured attachments modal with:
 * - Drag-and-drop file upload with version notes
 * - File type filtering
 * - Sort by date, size, or type
 * - Version history with restore
 * - Preview panel with side-by-side comparison
 * - Storage usage indicator
 * - Delete confirmation with version options
 */
const AttachmentsModal = ({
  isOpen,
  onClose,
  taskId,
  taskName,
  onAttachmentsChange,
}) => {
  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Note input state
  const [pendingNoteFile, setPendingNoteFile] = useState(null);

  // Data management hook
  const {
    attachments,
    filteredAttachments,
    storageUsed,
    storageLimit,
    maxFileSize,
    storagePercent,
    uniqueTypes,
    isLoading,
    error,
    filterType,
    setFilterType,
    sortBy,
    setSortBy,
    expandedVersions,
    toggleVersions,
    loadAttachments,
    addAttachment,
    removeAttachment,
    removeVersion,
    updateStorageUsed,
    setError,
    clearError,
  } = useAttachmentsData(taskId);

  // Actions hook
  const {
    previewFile,
    compareVersion,
    handlePreview,
    handleCompare,
    closePreview,
    deleteConfirm,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteAllConfirm,
    closeDeleteConfirm,
    handleDownload,
    handleRestore,
  } = useAttachmentsActions({
    taskId,
    attachments, // Pass for count calculation
    onAttachmentsChange,
    removeAttachment,
    removeVersion,
    updateStorageUsed,
    setError,
    loadAttachments,
  });

  // Handle successful upload
  const handleUploadComplete = useCallback((uploadedFile) => {
    addAttachment(uploadedFile);
    // Pass the new count (current + 1 since addAttachment hasn't updated state yet)
    onAttachmentsChange?.(attachments.length + 1);
  }, [addAttachment, onAttachmentsChange, attachments.length]);

  // Handle upload error
  const handleUploadError = useCallback((errorMessage) => {
    setError(errorMessage);
  }, [setError]);

  // Handle note request (before upload)
  const handleRequestNote = useCallback((file) => {
    setPendingNoteFile(file);
  }, []);

  // Upload handling hook
  const {
    isDragging,
    uploadProgress,
    isUploading,
    fileInputRef,
    dropZoneRef,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileSelect,
    openFilePicker,
    confirmUploadWithNote,
    cancelPendingUpload,
  } = useFileUpload({
    missionId: taskId,
    onUploadComplete: handleUploadComplete,
    onError: handleUploadError,
    onRequestNote: handleRequestNote,
  });

  // Animation effect
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      loadAttachments();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldAnimate(true);
        });
      });
    } else if (isVisible) {
      setShouldAnimate(false);
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible, loadAttachments]);

  // Sync attachment count to task when attachments finish loading (once per modal open)
  // This ensures the card shows the correct count even for existing attachments
  const hasSyncedCount = React.useRef(false);
  const hasStartedLoading = React.useRef(false);
  useEffect(() => {
    // Track when loading starts so we know to wait for it to complete
    if (isLoading) {
      hasStartedLoading.current = true;
    }
    // Only sync once when loading completes (after it started, not on initial render)
    if (!isLoading && hasStartedLoading.current && !hasSyncedCount.current && isOpen) {
      hasSyncedCount.current = true;
      onAttachmentsChange?.(attachments.length);
    }
    // Reset when modal closes
    if (!isOpen) {
      hasSyncedCount.current = false;
      hasStartedLoading.current = false;
    }
  }, [isLoading, isOpen, attachments.length, onAttachmentsChange]);

  // Note handlers
  const handleNoteConfirm = (note) => {
    setPendingNoteFile(null);
    confirmUploadWithNote(note);
  };

  const handleNoteCancel = () => {
    setPendingNoteFile(null);
    cancelPendingUpload();
  };

  if (!isVisible) return null;

  const content = (
    <div className={`attachments-modal ${shouldAnimate ? 'attachments-modal--open' : ''}`}>
      {/* Backdrop */}
      <div className="attachments-modal__backdrop" onClick={onClose} />
      
      {/* Modal */}
      <div className="attachments-modal__container">
        {/* Header */}
        <div className="attachments-modal__header">
          <div className="attachments-modal__header-left">
            <div className="attachments-modal__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </div>
            <div className="attachments-modal__title-group">
              <h2 className="attachments-modal__title">Attachments</h2>
              <span className="attachments-modal__subtitle">{taskName}</span>
            </div>
          </div>
          <button className="attachments-modal__close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="attachments-modal__error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            <button onClick={clearError}>×</button>
          </div>
        )}

        {/* Upload zone */}
        <DropZone
          dropZoneRef={dropZoneRef}
          fileInputRef={fileInputRef}
          isDragging={isDragging}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          maxFileSize={maxFileSize}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onFileSelect={handleFileSelect}
          onOpenFilePicker={openFilePicker}
        />

        {/* Storage indicator */}
        <StorageIndicator
          storageUsed={storageUsed}
          storageLimit={storageLimit}
          storagePercent={storagePercent}
        />

        {/* Filters & Sort */}
        {attachments.length > 0 && (
          <FilterControls
            filterType={filterType}
            setFilterType={setFilterType}
            sortBy={sortBy}
            setSortBy={setSortBy}
            uniqueTypes={uniqueTypes}
          />
        )}

        {/* File list */}
        <FileList
          isLoading={isLoading}
          filteredAttachments={filteredAttachments}
          expandedVersions={expandedVersions}
          onToggleVersions={toggleVersions}
          onDownload={handleDownload}
          onDelete={handleDeleteRequest}
          onPreview={handlePreview}
          onRestore={handleRestore}
          onCompare={handleCompare}
        />
      </div>

      {/* Preview Panel */}
      {previewFile && (
        <PreviewPanel
          missionId={taskId}
          file={previewFile}
          compareVersion={compareVersion}
          onClose={closePreview}
          onRestore={handleRestore}
        />
      )}

      {/* Note Input */}
      {pendingNoteFile && (
        <NoteInput
          filename={pendingNoteFile.name}
          onConfirm={handleNoteConfirm}
          onCancel={handleNoteCancel}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmModal
        isOpen={deleteConfirm.isOpen}
        file={deleteConfirm.file}
        versionId={deleteConfirm.versionId}
        onConfirm={handleDeleteConfirm}
        onConfirmAll={handleDeleteAllConfirm}
        onCancel={closeDeleteConfirm}
      />
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default AttachmentsModal;
